import { PortfolioScope, PortfolioScopeState } from 'types/portfolio';
import { t } from 'utils/getTranslations';

// Lookback window for the "Show only active clients" quick filter. Passed as the
// =isportfolioactiveclient= operator's value so the BE encodes the timecard-existence subquery
// against the same 36 months. Matches the "(3y)" copy on the Active clients summary card.
export const ACTIVE_CLIENT_LOOKBACK_MONTHS = 36;

// Seed the active-clients + exclude-PNG quick filters via the URL on first load. Also
// the state the page resets to when the portfolio scope changes (switching scope resets to the
// default sort/filter state).
export const INITIAL_SEARCH_PARAMS =
    `qf=id=isportfolioactiveclient=${ACTIVE_CLIENT_LOOKBACK_MONTHS};id=excludepersonanongrata=true` +
    `&sort=revenueYtd,desc`;

/**
 * Every scope-dependent string on the page comes in the same variants, so they all resolve through
 * one table instead of repeating the branching per string. `person` receives a `{name}` value;
 * `firm` is optional — a string with no firm-wide meaning omits it and resolves to null, which the
 * caller renders as "nothing" (the ownership legend, the card's "(… ownership)" suffix and the
 * group row's ownership line all disappear in firm scope, where no subset is "owned").
 */
type ScopeVariants = {
    my: string;
    team: string;
    person: string;
    selectedPeople: string;
    firm?: string;
};

/** A resolved message: an i18n key plus the values it interpolates. */
export type ScopeMessage = { id: string; values?: { name: string } };

const resolveScopeMessage = (state: PortfolioScopeState, variants: ScopeVariants): ScopeMessage | null => {
    switch (state.scope) {
        case PortfolioScope.FIRM:
            return variants.firm ? { id: variants.firm } : null;
        case PortfolioScope.TEAM:
            return { id: variants.team };
        case PortfolioScope.USERS:
            if (state.people.length === 0) return { id: variants.my };
            // A single resolved name is spelled out; several people — or a name still resolving from
            // the URL, which would render as "'s ownership" — fall back to the generic variant.
            if (state.people.length === 1 && state.people[0].name) {
                return { id: variants.person, values: { name: state.people[0].name } };
            }
            return { id: variants.selectedPeople };
        default:
            return { id: variants.my };
    }
};

const scopeText = (state: PortfolioScopeState, variants: ScopeVariants): string | null => {
    const message = resolveScopeMessage(state, variants);
    return message ? t(message.id, message.values) : null;
};

// Page heading / breadcrumb for the active scope. MY (and People before anyone is
// picked) keeps the original "My Portfolio" title.
export const buildPortfolioHeading = (state: PortfolioScopeState): string => {
    switch (state.scope) {
        case PortfolioScope.TEAM:
            return t('portfolio.heading.team');
        case PortfolioScope.FIRM:
            return t('portfolio.heading.firm');
        case PortfolioScope.USERS: {
            // Only join names that have resolved (the URL seeds ids with empty names until the picker
            // enriches them) so the heading reads "My Portfolio" while loading, then the names —
            // never "'s Portfolio" or " + 's Portfolio".
            const names = state.people.map((person) => person.name).filter(Boolean);
            return names.length > 0
                ? t('portfolio.heading.persons', { names: names.join(' + ') })
                : t('portfolio.my-portfolio');
        }
        default:
            return t('portfolio.my-portfolio');
    }
};

// The orange side-border legend label for the active scope. Null for FIRM, where the
// border and its label are dropped.
const LEGEND_VARIANTS: ScopeVariants = {
    my: 'portfolio.legend.my-clients',
    team: 'portfolio.legend.team-clients',
    person: 'portfolio.legend.persons-clients',
    selectedPeople: 'portfolio.legend.selected-people-clients',
};

export const buildPortfolioLegendLabel = (state: PortfolioScopeState): string | null =>
    scopeText(state, LEGEND_VARIANTS);

// The "(… ownership)" qualifier appended to the revenue / outstanding summary-card headings, telling
// the reader whose numbers the card sums.
const CARD_OWNERSHIP_VARIANTS: ScopeVariants = {
    my: 'portfolio.cards.ownership.my',
    team: 'portfolio.cards.ownership.team',
    person: 'portfolio.cards.ownership.person',
    selectedPeople: 'portfolio.cards.ownership.selected-people',
};

export const buildCardOwnershipLabel = (state: PortfolioScopeState): string | null =>
    scopeText(state, CARD_OWNERSHIP_VARIANTS);

// Client-count card tooltips. Both explain WHOSE RP/RM relationships are being counted, so the firm
// variant says the opposite: every client and lead, whoever owns it.
const TOTAL_CLIENTS_TOOLTIP_VARIANTS: ScopeVariants = {
    my: 'portfolio.cards.total-clients-tooltip.my',
    team: 'portfolio.cards.total-clients-tooltip.team',
    person: 'portfolio.cards.total-clients-tooltip.person',
    selectedPeople: 'portfolio.cards.total-clients-tooltip.selected-people',
    firm: 'portfolio.cards.total-clients-tooltip.firm',
};

const ACTIVE_CLIENTS_TOOLTIP_VARIANTS: ScopeVariants = {
    my: 'portfolio.cards.active-clients-tooltip.my',
    team: 'portfolio.cards.active-clients-tooltip.team',
    person: 'portfolio.cards.active-clients-tooltip.person',
    selectedPeople: 'portfolio.cards.active-clients-tooltip.selected-people',
    firm: 'portfolio.cards.active-clients-tooltip.firm',
};

// Both card tooltips resolve to a message rather than text: LabelCard takes an i18n key plus values.
export const buildTotalClientsTooltip = (state: PortfolioScopeState): ScopeMessage | null =>
    resolveScopeMessage(state, TOTAL_CLIENTS_TOOLTIP_VARIANTS);

export const buildActiveClientsTooltip = (state: PortfolioScopeState): ScopeMessage | null =>
    resolveScopeMessage(state, ACTIVE_CLIENTS_TOOLTIP_VARIANTS);

// "Show only my ownership" quick filter (groups view only). No firm variant: the backend drops the
// filter in firm scope (there is no "mine" to narrow to), so the checkbox is not rendered there.
const OWNERSHIP_FILTER_VARIANTS: ScopeVariants = {
    my: 'portfolio.filter.ownership-only.my',
    team: 'portfolio.filter.ownership-only.team',
    person: 'portfolio.filter.ownership-only.person',
    selectedPeople: 'portfolio.filter.ownership-only.selected-people',
};

export const buildOwnershipFilterLabel = (state: PortfolioScopeState): string | null =>
    scopeText(state, OWNERSHIP_FILTER_VARIANTS);

// The quick filter's own tooltip, named the same way as its label.
const OWNERSHIP_FILTER_TOOLTIP_VARIANTS: ScopeVariants = {
    my: 'portfolio.filter.ownership-only-tooltip.my',
    team: 'portfolio.filter.ownership-only-tooltip.team',
    person: 'portfolio.filter.ownership-only-tooltip.person',
    selectedPeople: 'portfolio.filter.ownership-only-tooltip.selected-people',
};

export const buildOwnershipFilterTooltip = (state: PortfolioScopeState): ScopeMessage | null =>
    resolveScopeMessage(state, OWNERSHIP_FILTER_TOOLTIP_VARIANTS);

// The secondary "<owner> ownership: <amount>" line under a group row's YTD revenue: the slice of the
// group's revenue owned by the current scope. Null for FIRM — the backend sums that slice over an
// empty ownership set there, so the line would always read 0.
const OWNERSHIP_LINE_VARIANTS: ScopeVariants = {
    my: 'portfolio.columns.ownership-line.my',
    team: 'portfolio.columns.ownership-line.team',
    person: 'portfolio.columns.ownership-line.person',
    selectedPeople: 'portfolio.columns.ownership-line.selected-people',
};

export const buildOwnershipLineMessage = (state: PortfolioScopeState): ScopeMessage | null =>
    resolveScopeMessage(state, OWNERSHIP_LINE_VARIANTS);

// The page-heading info tooltip: a scope-aware opening paragraph followed by the two view-mode
// paragraphs, which are scope-neutral.
const HEADING_INTRO_VARIANTS: ScopeVariants = {
    my: 'portfolio.heading-tooltip.intro.my',
    team: 'portfolio.heading-tooltip.intro.team',
    person: 'portfolio.heading-tooltip.intro.person',
    selectedPeople: 'portfolio.heading-tooltip.intro.selected-people',
    firm: 'portfolio.heading-tooltip.intro.firm',
};

export const buildHeadingTooltipIntro = (state: PortfolioScopeState): ScopeMessage | null =>
    resolveScopeMessage(state, HEADING_INTRO_VARIANTS);

/**
 * A stable key for the active scope *type* — used to remount the table so switching scope type
 * resets its filters/sort. Deliberately excludes the selected people: adding/removing colleagues
 * within the "People" scope must NOT remount, or the open multiselect dropdown would tear down
 * mid-selection. Those changes refetch the table via MyPortfolioTable's scopeRefetchKey instead.
 */
export const portfolioScopeKey = (state: PortfolioScopeState): string => state.scope;
